import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  CalendarClock,
  CircleAlert,
  CircleCheck,
  Download,
  Eye,
  FileSearch,
  FileText,
  Info,
  Layers,
  ListChecks,
  ShieldCheck,
  TriangleAlert,
  Upload,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Pill } from '@/components/ui/pill';
import { Spinner } from '@/components/ui/feedback';
import { RecordRuler } from '@/features/converter/record-ruler';
import { CONTACT_URL, XmlPreview } from '@/features/converter/xml-preview';
import {
  MAX_FILE_BYTES,
  RECORD_LENGTH,
  convertFireFile,
  download,
  toCsv,
  toExceptionsCsv,
  toForeignAddressCsv,
  toIrisXml,
  type ConversionResult,
  type Finding,
  type NameOverrides,
  type XmlDocument,
} from '@/features/converter/convert';
import { formatBytes, formatCount } from '@/lib/format';
import { cn } from '@/lib/cn';
import { LOGO_URL } from '@/lib/asset';

export const Route = createFileRoute('/tools/fire-to-iris')({
  component: ConverterPage,
});

/**
 * Read a FIRE (Publication 1220) file, say what is wrong with it, and preview the IRIS XML.
 * Everything happens in this tab: the file is read in the browser and never sent anywhere.
 * The reading and the XML are the engine Tab Service files with. The XML is shown, not
 * downloaded: the complete file comes from Tab Service (see XmlPreview).
 *
 * Dressed like the rest of Tab Service: the sign-in screen's blue ground behind the opening,
 * white cards with the navy-tinted shadows, and the theme tokens throughout so the dark
 * theme follows.
 */
function ConverterPage() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ConversionResult | null>(null);
  const [overrides, setOverrides] = useState<NameOverrides>({});
  const [documents, setDocuments] = useState<XmlDocument[] | null>(null);
  const [xmlError, setXmlError] = useState<string | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const read = useCallback(async (file: File) => {
    setBusy(true);
    setReadError(null);
    setXmlError(null);
    setDocuments(null);
    setOverrides({});
    try {
      if (file.size > MAX_FILE_BYTES) {
        throw new Error(
          `That file is ${formatBytes(file.size)}. This page reads up to ${formatBytes(MAX_FILE_BYTES)}, which is ` +
            'also the most the IRS takes in one transmission. Split it and read the parts.',
        );
      }
      const text = await file.text();
      if (!text.trim()) throw new Error('That file is empty.');
      const converted = convertFireFile(file.name, text);
      setResult(converted);
      window.scrollTo({ top: 0 });
    } catch (error) {
      setResult(null);
      setReadError(error instanceof Error ? error.message : 'That file could not be read.');
    } finally {
      setBusy(false);
    }
  }, []);

  async function buildXml(current: ConversionResult) {
    setBusy(true);
    setXmlError(null);
    try {
      setDocuments(await toIrisXml(current, overrides));
    } catch (error) {
      setXmlError(error instanceof Error ? error.message : 'The XML could not be written.');
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setResult(null);
    setDocuments(null);
    setOverrides({});
  }

  return (
    <div className="min-h-screen bg-[color:var(--ts-app-bg)] text-foreground">
      <TopBar onHome={result ? reset : undefined} />

      {result ? (
        <main className="mx-auto w-full max-w-[1240px] px-4 sm:px-6">
          <Report
            result={result}
            overrides={overrides}
            documents={documents}
            busy={busy}
            xmlError={xmlError}
            onOverride={(ref, value) => setOverrides((old) => ({ ...old, [ref]: value }))}
            onBuild={() => void buildXml(result)}
            onReset={reset}
          />
        </main>
      ) : (
        <main>
          <Intro
            busy={busy}
            dragging={dragging}
            error={readError}
            onDragging={setDragging}
            onChoose={(file) => void read(file)}
            inputRef={fileInput}
          />
        </main>
      )}

      <Footer />
    </div>
  );
}

// ---------------------------------------------------------------------------

function TopBar({ onHome }: { onHome?: () => void }) {
  return (
    <header className="sticky top-0 z-30 border-b border-[color:var(--ts-border-soft)] bg-[color:var(--ts-surface)]/90 shadow-[var(--ts-shadow-header)] backdrop-blur">
      <div className="mx-auto flex w-full max-w-[1240px] items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <div className="flex min-w-0 items-center gap-4">
          <img src={LOGO_URL} alt="Tab Service" className="h-9 w-auto shrink-0" />
          <span aria-hidden="true" className="hidden h-6 w-px bg-[color:var(--ts-border)] sm:block" />
          <button
            type="button"
            onClick={onHome}
            disabled={!onHome}
            className="hidden items-center gap-2 text-base font-semibold text-foreground enabled:hover:text-primary sm:inline-flex"
          >
            FIRE to IRIS converter
          </button>
        </div>
        <nav className="flex items-center gap-1 sm:gap-2">
          {onHome ? null : (
            <>
              <a href="#how" className="hidden rounded-md px-3 py-2 text-base text-muted-foreground hover:text-primary md:block">
                How it works
              </a>
              <a href="#records" className="hidden rounded-md px-3 py-2 text-base text-muted-foreground hover:text-primary md:block">
                The 1220 format
              </a>
            </>
          )}
          <a
            href={CONTACT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden rounded-md px-3 py-2 text-base text-muted-foreground hover:text-primary sm:block"
          >
            Contact
          </a>
          <Button asChild variant="outline" size="md">
            <Link to="/sign-in">Sign in</Link>
          </Button>
        </nav>
      </div>
    </header>
  );
}

/**
 * The six record types a Publication 1220 file is made of, and what becomes of each one,
 * laid out the way the publication lays out a record: a code, a name, a count, a meaning.
 */
const RECORD_TYPES: { code: string; name: string; length: string; fate: string }[] = [
  {
    code: 'T',
    name: 'Transmitter',
    length: 'one, first',
    fate: 'Becomes the IRIS transmitter details. The TCC comes with it; the test indicator carries over.',
  },
  {
    code: 'A',
    name: 'Issuer',
    length: 'one per issuer',
    fate: 'Names the payer and, in its Type of Return, the form. Its amount codes say which payment fields carry money.',
  },
  {
    code: 'B',
    name: 'Payee',
    length: '750 characters each',
    fate: 'One return. Positions 1 to 543 are the same on every form; 544 to 750 are the form itself.',
  },
  {
    code: 'C',
    name: 'End of issuer',
    length: 'one per issuer',
    fate: 'Control totals. Checked against what was actually read, then dropped: IRIS totals its own submission.',
  },
  {
    code: 'K',
    name: 'State totals',
    length: 'optional',
    fate: 'No IRIS equivalent. Combined Federal/State filing is a setting on the submission, not a record.',
  },
  {
    code: 'F',
    name: 'End of transmission',
    length: 'one, last',
    fate: 'The payee count. Checked, then dropped.',
  },
];

const STEPS = [
  {
    icon: Upload,
    title: 'Drop your 1220 file',
    detail: 'The flat file your system already writes for FIRE. It is read here, in this tab, and never uploaded.',
  },
  {
    icon: FileSearch,
    title: 'Review what was found',
    detail: 'Every record at its real positions, every payee against the IRS rules. Each finding names the line and the fix.',
  },
  {
    icon: Eye,
    title: 'Preview the IRIS XML',
    detail: 'See the transmissions your file becomes, split the way the IRS expects. Tab Service files the full XML.',
  },
] as const;

interface DropProps {
  busy: boolean;
  dragging: boolean;
  error: string | null;
  onDragging: (value: boolean) => void;
  onChoose: (file: File) => void;
  inputRef: React.RefObject<HTMLInputElement>;
}

function Intro(props: DropProps) {
  return (
    <>
      {/* The opening: the sign-in screen's ground, the promise on the left, the tool on the right. */}
      <section className="relative overflow-hidden border-b border-[color:var(--ts-border-soft)] bg-gradient-to-br from-[color:var(--ts-ground-top)] to-[color:var(--ts-ground-bottom)]">
        <HeroBackdrop />
        <div className="relative mx-auto grid w-full max-w-[1240px] items-center gap-10 px-4 pt-14 pb-36 sm:px-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:gap-14 lg:pt-20">
          <div className="anim-rise">
            <span className="inline-flex items-center gap-2 rounded-full border border-[color:var(--ts-pill-warning-border)] bg-[color:var(--ts-pill-warning-bg)] px-3 py-1 text-sm font-medium text-[color:var(--ts-pill-warning-text)]">
              <CalendarClock className="h-4 w-4" aria-hidden="true" />
              FIRE stops accepting files after 19 November 2026
            </span>
            <h1 className="mt-5 max-w-[16ch] text-[2.4rem] leading-[1.08] font-semibold tracking-tight text-foreground [text-wrap:balance] sm:text-[3.1rem]">
              Convert your FIRE file to <span className="text-primary">IRIS XML</span>
            </h1>
            <p className="mt-5 max-w-[54ch] text-lg leading-7 text-muted-foreground">
              Drop in the Publication 1220 file your system already writes. Every record is read at its real
              positions, every payee is checked against the rules the IRS applies, and you see the IRIS XML it
              becomes.
            </p>
            <ul className="mt-8 grid gap-4 sm:grid-cols-3">
              {[
                { icon: ShieldCheck, text: 'Read in your browser, never uploaded' },
                { icon: Layers, text: 'The engine Tab Service files with' },
                { icon: ListChecks, text: 'Checked against the IRS rules' },
              ].map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[color:var(--ts-surface)] text-primary shadow-[var(--ts-shadow-raised)]">
                    <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
                  </span>
                  <span className="pt-2 text-base leading-5 text-foreground">{text}</span>
                </li>
              ))}
            </ul>
          </div>

          <UploadCard {...props} />
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1240px] px-4 sm:px-6">
        {/* How it works: three steps, in the order a person takes them. */}
        <section id="how" className="scroll-mt-20 pt-20">
          <SectionHeading
            eyebrow="How it works"
            title="From a 1220 file to IRIS in three steps"
            detail="No account, no install. The same reading and the same XML generators Tab Service uses to file with the IRS."
          />
          <ol className="mt-10 grid gap-5 md:grid-cols-3">
            {STEPS.map(({ icon: Icon, title, detail }, index) => (
              <li
                key={title}
                className="rounded-lg border border-[color:var(--ts-border-soft)] bg-[color:var(--ts-surface)] p-6 shadow-[var(--ts-shadow-raised)]"
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-[color:var(--ts-primary-light)] text-primary">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span className="font-mono text-sm text-muted-foreground">Step {index + 1}</span>
                </div>
                <h3 className="mt-5 text-lg font-semibold text-foreground">{title}</h3>
                <p className="mt-2 text-base leading-6 text-muted-foreground">{detail}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* The format, explained: the six record types. */}
        <section id="records" className="scroll-mt-20 pt-20">
          <SectionHeading
            eyebrow={`Publication 1220 · ${RECORD_LENGTH} characters a line`}
            title="What is in a FIRE file"
            detail="Six kinds of line, told apart by the first character. Here is each one, and what becomes of it in IRIS."
          />
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {RECORD_TYPES.map((record) => (
              <article
                key={record.code}
                className="rounded-lg border border-[color:var(--ts-border-soft)] bg-[color:var(--ts-surface)] p-5 transition-shadow hover:shadow-[var(--ts-shadow-raised)]"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md border border-[color:var(--ts-border)] bg-[color:var(--ts-blue-bglayout)] font-mono text-lg font-semibold text-primary">
                    {record.code}
                  </span>
                  <div>
                    <h3 className="text-base font-semibold text-foreground">{record.name}</h3>
                    <p className="font-mono text-xs text-muted-foreground">{record.length}</p>
                  </div>
                </div>
                <p className="mt-4 text-base leading-6 text-muted-foreground">{record.fate}</p>
              </article>
            ))}
          </div>
        </section>

        {/* The payee record at its positions: the explanation the six cards lead up to. */}
        <section className="pt-20">
          <div className="rounded-lg border border-[color:var(--ts-border-soft)] bg-[color:var(--ts-surface)] p-6 shadow-[var(--ts-shadow-raised)] sm:p-8">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-foreground">A payee record, at its positions</h2>
                <p className="mt-1 max-w-[68ch] text-base text-muted-foreground">
                  750 characters where a fact means what it means because of where it sits. Read a file and this
                  fills with your own first payee, with what IRIS calls the same fact underneath.
                </p>
              </div>
              <span className="font-mono text-xs text-muted-foreground">scroll sideways for the rest of the 750</span>
            </div>
            <div className="relative mt-6">
              <RecordRuler />
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-[color:var(--ts-surface)] to-transparent"
              />
            </div>
          </div>
        </section>

        <DeadlineBand />
      </div>
    </>
  );
}

/**
 * Invented records in Publication 1220 layout, for the backdrop only: made-up names, made-up
 * TINs and round amounts. Nothing here is anybody's data.
 */
const BACKDROP_LINES = [
  'B2025 900000002 PUB-1220-001 000000150000 000000000000 RIVERA JORDAN A       14 ELM AVE     DENVER CO80203',
  'A2025 900000001 PARK NEC1 4  NORTHFIELD SUPPLY CO     200 LAKE ST          SPRINGFIELD      IL62701 1',
  'B2025 900000004 PUB-1220-003 000000430000 000000000000 NGUYEN MINH T         77 PINE ST     SEATTLE WA98101',
] as const;

/**
 * Behind the opening: two soft pools of the brand blue, and a few lines of a 1220 file
 * drifting along the foot, below the content and faded at both edges, so the eye reads them
 * as texture first.
 */
function HeroBackdrop() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(45% 60% at 88% 18%, color-mix(in srgb, var(--ts-primary) 16%, transparent), transparent 70%), ' +
            'radial-gradient(40% 55% at 8% 95%, color-mix(in srgb, var(--ts-blue-234f92) 10%, transparent), transparent 70%)',
        }}
      />
      <div
        className="absolute inset-x-0 bottom-6 flex flex-col gap-1.5 font-mono text-[13px] leading-5 whitespace-nowrap text-primary opacity-[0.11]"
        style={{
          maskImage: 'linear-gradient(to right, transparent, black 18%, black 82%, transparent)',
          WebkitMaskImage: 'linear-gradient(to right, transparent, black 18%, black 82%, transparent)',
        }}
      >
        {BACKDROP_LINES.map((line, index) => (
          <div key={line} className={cn('flex w-max', index % 2 === 0 ? 'anim-drift' : 'anim-drift-reverse')}>
            <span className="pr-16">{line}</span>
            <span className="pr-16">{line}</span>
            <span className="pr-16">{line}</span>
            <span className="pr-16">{line}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionHeading({ eyebrow, title, detail }: { eyebrow: string; title: string; detail: string }) {
  return (
    <div className="max-w-[64ch]">
      <p className="text-sm font-semibold tracking-[0.08em] text-primary uppercase">{eyebrow}</p>
      <h2 className="mt-2 text-[1.75rem] leading-9 font-semibold tracking-tight text-foreground [text-wrap:balance]">
        {title}
      </h2>
      <p className="mt-3 text-lg leading-7 text-muted-foreground">{detail}</p>
    </div>
  );
}

function UploadCard({ busy, dragging, error, onDragging, onChoose, inputRef }: DropProps) {
  return (
    <div className="anim-rise rounded-xl border border-[color:var(--ts-border-soft)] bg-[color:var(--ts-surface)] p-5 shadow-[var(--ts-shadow-float)] sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-lg font-semibold text-foreground">Check a file</p>
          <p className="text-sm text-muted-foreground">Publication 1220 · .txt or .dat</p>
        </div>
        <Pill tone="success">
          <ShieldCheck className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
          Stays on this device
        </Pill>
      </div>

      <section
        onDragOver={(event) => {
          event.preventDefault();
          onDragging(true);
        }}
        onDragLeave={() => onDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          onDragging(false);
          const file = event.dataTransfer.files?.[0];
          if (file) onChoose(file);
        }}
        aria-label="Drop a Publication 1220 file here"
        className={cn(
          'mt-5 flex flex-col items-center gap-4 rounded-lg border-2 border-dashed px-6 py-10 text-center',
          'transition-[border-color,background-color] duration-200 ease-out',
          dragging
            ? 'border-primary bg-[color:var(--ts-blue-bglayout)]'
            : 'border-[color:var(--ts-border)] bg-[color:var(--ts-blue-bglayout)] hover:border-[color:var(--ts-blue-234f92)]',
        )}
      >
        <span
          className={cn(
            'flex h-14 w-14 items-center justify-center rounded-full transition-colors duration-200',
            dragging ? 'bg-primary text-primary-foreground' : 'bg-[color:var(--ts-primary-light)] text-primary',
          )}
        >
          {busy ? <Spinner className="h-6 w-6" /> : <Upload className="h-6 w-6" aria-hidden="true" />}
        </span>
        <div>
          <p className="text-lg font-semibold text-foreground">
            {busy ? 'Reading your file' : dragging ? 'Let go to read it' : 'Drop your 1220 file here'}
          </p>
          <p className="mt-1 text-base text-muted-foreground">
            {busy ? 'It is being read in this tab.' : `or choose it from your computer · up to ${formatBytes(MAX_FILE_BYTES)}`}
          </p>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".txt,.dat,text/plain"
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) onChoose(file);
            event.target.value = '';
          }}
        />
        <Button variant="primary" size="lg" onClick={() => inputRef.current?.click()} disabled={busy}>
          Choose a file
        </Button>
      </section>

      {error ? (
        <p
          role="alert"
          className="mt-4 flex gap-2 rounded-md border border-[color:var(--ts-pill-danger-border)] bg-[color:var(--ts-pill-danger-bg)] px-3 py-2 text-base text-[color:var(--ts-pill-danger-text)]"
        >
          <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      ) : null}

      <div className="mt-5 grid grid-cols-3 gap-2 border-t border-[color:var(--ts-border-soft)] pt-4 text-center">
        {[
          { icon: FileSearch, label: 'Findings report' },
          { icon: FileText, label: 'Records spreadsheet' },
          { icon: Eye, label: 'IRIS XML preview' },
        ].map(({ icon: Icon, label }) => (
          <div key={label} className="flex flex-col items-center gap-1.5">
            <Icon className="h-4 w-4 text-primary" aria-hidden="true" />
            <span className="text-sm text-muted-foreground">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** The date that makes this page matter, and who can take the work off your hands. */
function DeadlineBand() {
  return (
    <section className="relative mt-20 overflow-hidden rounded-xl bg-primary text-primary-foreground">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(55% 90% at 100% 0%, rgb(255 255 255 / 0.16), transparent 70%), ' +
            'radial-gradient(40% 80% at 0% 100%, rgb(255 255 255 / 0.08), transparent 70%)',
        }}
      />
      <div className="relative grid gap-8 px-6 py-10 sm:px-10 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
        <div>
          <p className="text-sm font-semibold tracking-[0.08em] uppercase opacity-80">The clock</p>
          <h2 className="mt-2 max-w-[30ch] text-[1.75rem] leading-9 font-semibold tracking-tight [text-wrap:balance]">
            FIRE closes after 19 November 2026. Tax year 2026 returns go to IRIS.
          </h2>
          <p className="mt-3 max-w-[62ch] text-lg leading-7 opacity-85">
            IRIS needs its own transmitter code, a connection to the IRS and somewhere to keep the receipts. Tab
            Service does all three under its own IRIS TCC, from the files you already produce.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <a
            href={CONTACT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-10 items-center gap-2 rounded-md bg-primary-foreground px-5 text-base font-medium text-primary hover:opacity-90"
          >
            Contact Tab Service
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </a>
          <Link
            to="/sign-in"
            className="inline-flex h-10 items-center rounded-md border border-current/40 px-5 text-base font-medium hover:bg-white/10"
          >
            Sign in
          </Link>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------

function Report({
  result,
  overrides,
  documents,
  busy,
  xmlError,
  onOverride,
  onBuild,
  onReset,
}: {
  result: ConversionResult;
  overrides: NameOverrides;
  documents: XmlDocument[] | null;
  busy: boolean;
  xmlError: string | null;
  onOverride: (recordRef: string, value: { first: string; last: string }) => void;
  onBuild: () => void;
  onReset: () => void;
}) {
  const blocking = result.findings.filter((finding) => finding.severity === 'blocking');
  const attention = result.findings.filter((finding) => finding.severity === 'attention');
  const notes = result.findings.filter((finding) => finding.severity === 'note');
  const checkNames = useMemo(() => result.names.filter((name) => name.check), [result.names]);
  const base = result.fileName.replace(/\.[^.]+$/, '');

  return (
    <div className="anim-rise flex flex-col gap-6 pt-6 pb-10">
      {/* The file, and where it stands. */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[color:var(--ts-primary-light)] text-primary">
            <FileText className="h-6 w-6" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-2xl font-semibold text-foreground">{result.fileName}</h1>
              {result.convertible ? <Pill tone="success">Ready to convert</Pill> : <Pill tone="danger">Blocked</Pill>}
              {result.transmitter.testFile ? <Pill tone="info">Test file</Pill> : null}
            </div>
            <p className="mt-0.5 text-base text-muted-foreground">
              {formatCount(result.lines)} records read, {formatBytes(result.bytes)}. Nothing left this browser.
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={onReset}>
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Read another file
        </Button>
      </div>

      <section aria-labelledby="facts-heading">
        <h2 id="facts-heading" className="text-sm font-medium text-muted-foreground">
          What the file says about itself
        </h2>
        <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile label="Tax year" value={result.taxYear ?? 'Not stated'} />
          <StatTile label="Payees" value={formatCount(result.payees)} />
          <StatTile label="Issuers" value={formatCount(result.issuers.length)} />
          <StatTile
            label="Transmitter"
            value={result.transmitter.name || 'Not named'}
            hint={result.transmitter.tcc ? `TCC ${result.transmitter.tcc}` : 'No TCC in the file'}
          />
        </div>
      </section>

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-6">
          <Card
            title="What was found"
            action={
              <div className="flex flex-wrap gap-2">
                <Count tone="danger" n={blocking.length} label="blocking" />
                <Count tone="warning" n={attention.length} label="to decide" />
                <Count tone="info" n={notes.length} label="worth knowing" />
              </div>
            }
          >
            {result.findings.length === 0 ? (
              <div className="flex items-start gap-3 rounded-md bg-[color:var(--ts-pill-success-bg)] px-4 py-3 text-[color:var(--ts-pill-success-text)]">
                <CircleCheck className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <p className="text-base">
                  Nothing to report. Every record is the right length, every payee has a name and a taxpayer ID,
                  and every form converts.
                </p>
              </div>
            ) : (
              <ul className="flex flex-col gap-2.5">
                {[...blocking, ...attention, ...notes].slice(0, 40).map((finding, index) => (
                  <FindingRow key={`${finding.code}-${finding.line ?? index}`} finding={finding} />
                ))}
                {result.findings.length > 40 ? (
                  <li className="text-base text-muted-foreground">
                    {formatCount(result.findings.length - 40)} more in the exception report.
                  </li>
                ) : null}
              </ul>
            )}
          </Card>

          {checkNames.length > 0 ? (
            <Card
              title="Names worth a second look"
              detail="FIRE writes one name line, surname first. IRIS wants the parts separately. These splits are a judgement call; correct any that read wrongly and your corrections go into the XML and the spreadsheet."
            >
              <div className="-mx-6 overflow-x-auto">
                <table className="w-full text-base">
                  <thead className="bg-[color:var(--ts-thead-bg)] text-left text-sm text-muted-foreground">
                    <tr>
                      <th className="py-2.5 pr-4 pl-6 font-medium">In the file</th>
                      <th className="py-2.5 pr-4 font-medium">First name</th>
                      <th className="py-2.5 pr-4 font-medium">Last name</th>
                      <th className="py-2.5 pr-6 font-medium">Why it is here</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[color:var(--ts-row-border)]">
                    {checkNames.slice(0, 60).map((name) => {
                      const fix = overrides[name.recordRef];
                      return (
                        <tr key={name.recordRef}>
                          <td className="py-2 pr-4 pl-6 font-mono text-sm whitespace-nowrap">{name.original}</td>
                          <td className="py-2 pr-4">
                            <NameInput
                              label={`First name for ${name.original}`}
                              value={fix?.first ?? name.first}
                              onChange={(value) => onOverride(name.recordRef, { first: value, last: fix?.last ?? name.last })}
                            />
                          </td>
                          <td className="py-2 pr-4">
                            <NameInput
                              label={`Last name for ${name.original}`}
                              value={fix?.last ?? name.last}
                              onChange={(value) => onOverride(name.recordRef, { first: fix?.first ?? name.first, last: value })}
                            />
                          </td>
                          <td className="py-2 pr-6 text-base text-muted-foreground">{name.why}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {checkNames.length > 60 ? (
                <p className="mt-3 text-base text-muted-foreground">
                  Showing the first 60 of {formatCount(checkNames.length)}. The rest are in the spreadsheet.
                </p>
              ) : null}
            </Card>
          ) : null}

          {result.sampleRecordLine ? (
            <Card
              title="Your first payee record, at its positions"
              detail="Line one of your payees, cut where Publication 1220 cuts it, with what IRIS calls the same fact underneath. Useful when a record is refused and you need to see what is really in it."
            >
              <RecordRuler line={result.sampleRecordLine} />
            </Card>
          ) : null}

          {result.amounts.length > 0 ? (
            <Card title="Where the money lands" detail="Each amount code in your file, and the IRIS field it becomes.">
              <div className="grid gap-2 sm:grid-cols-2">
                {result.amounts.map((amount) => (
                  <div
                    key={`${amount.formType}-${amount.code}`}
                    className="flex flex-wrap items-center gap-2 rounded-md border border-[color:var(--ts-row-border)] px-3 py-2 text-base"
                  >
                    <span className="rounded bg-[color:var(--ts-blue-bglayout)] px-1.5 py-0.5 font-mono text-sm text-primary">
                      {amount.code}
                    </span>
                    <span className="text-muted-foreground">{amount.formType}</span>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                    <span className="font-mono text-sm text-foreground">{amount.box}</span>
                    <span className="ml-auto text-sm text-muted-foreground">{formatCount(amount.payees)} payees</span>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}
        </div>

        {/* Where the file stands and what to do with it, kept in view. */}
        <aside className="flex flex-col gap-4 lg:sticky lg:top-20">
          <div className="rounded-lg border border-[color:var(--ts-border-soft)] bg-[color:var(--ts-surface)] p-5 shadow-[var(--ts-shadow-raised)]">
            <h2 className="text-base font-semibold text-foreground">The IRIS XML</h2>
            {result.convertible ? (
              <p className="mt-1 text-base text-muted-foreground">
                See the IRIS payload these records become, written with your transmitter details from the T record.
                The structure, data and mapping are checked here; the IRS schema check runs when Tab Service files.
              </p>
            ) : (
              <p className="mt-1 text-base text-[color:var(--ts-pill-danger-text)]">
                Fix the blocking findings before this file can become IRIS XML. The exception report lists every
                one of them.
              </p>
            )}

            <div className="mt-4 flex flex-col gap-2">
              <Button variant="primary" size="lg" onClick={onBuild} disabled={!result.convertible || busy} className="w-full">
                {busy ? <Spinner className="h-4 w-4" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                {documents ? 'Preview the XML again' : 'Preview the IRIS XML'}
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => download(`${base}-iris-records.csv`, toCsv(result, overrides), 'text/csv')}
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                Records spreadsheet
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => download(`${base}-exceptions.csv`, toExceptionsCsv(result), 'text/csv')}
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                Exception report
              </Button>
              {result.foreignAddresses.length > 0 ? (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => download(`${base}-foreign-addresses.csv`, toForeignAddressCsv(result), 'text/csv')}
                >
                  <Download className="h-4 w-4" aria-hidden="true" />
                  Foreign address report
                </Button>
              ) : null}
            </div>

            {xmlError ? <p className="mt-3 text-base text-[color:var(--ts-pill-danger-text)]">{xmlError}</p> : null}

            <div className="mt-5 border-t border-[color:var(--ts-border-soft)] pt-4">
              <p className="text-sm font-medium text-muted-foreground">Forms in this file</p>
              <div className="mt-2 flex flex-col gap-2">
                {result.forms.map((form) => (
                  <div key={form.rawTypeOfReturn} className="flex flex-wrap items-center justify-between gap-2 text-base">
                    <span className="font-medium text-foreground">{form.formType}</span>
                    <span className="flex items-center gap-2 text-sm text-muted-foreground">
                      {formatCount(form.payees)} payees · {form.transmissions} transmission
                      {form.transmissions === 1 ? '' : 's'}
                      {form.supported ? null : <Pill tone="danger">not converted</Pill>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-[color:var(--ts-pill-info-border)] bg-[color:var(--ts-blue-bglayout)] p-5">
            <h2 className="text-base font-semibold text-foreground">Have Tab Service file it</h2>
            <ul className="mt-3 flex flex-col gap-2 text-base text-muted-foreground">
              {[
                'The complete IRIS XML, under our IRIS TCC',
                'Transmission and IRS receipts kept for you',
                'Corrections and replacements handled',
              ].map((line) => (
                <li key={line} className="flex gap-2">
                  <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  {line}
                </li>
              ))}
            </ul>
            <a
              href={CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-1.5 text-base font-medium text-link hover:underline"
            >
              Contact Tab Service
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>
        </aside>
      </div>

      {documents && documents.length > 0 ? <XmlPreview documents={documents} fileName={result.fileName} /> : null}
    </div>
  );
}

function Card({
  title,
  detail,
  action,
  children,
}: {
  title: string;
  detail?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-[color:var(--ts-border-soft)] bg-[color:var(--ts-surface)] p-6 shadow-[var(--ts-shadow-card)]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          {detail ? <p className="mt-1 max-w-[72ch] text-base text-muted-foreground">{detail}</p> : null}
        </div>
        {action}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function StatTile({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="rounded-lg border border-[color:var(--ts-border-soft)] bg-[color:var(--ts-surface)] px-5 py-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="mt-1 truncate text-xl font-semibold text-foreground tabular-nums">{value}</p>
      {hint ? <p className="mt-0.5 font-mono text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function Count({ n, label, tone }: { n: number; label: string; tone: 'danger' | 'warning' | 'info' }) {
  if (n === 0) return null;
  return (
    <Pill tone={tone}>
      {formatCount(n)} {label}
    </Pill>
  );
}

const SEVERITY = {
  blocking: {
    icon: CircleAlert,
    text: 'text-[color:var(--ts-pill-danger-text)]',
    stripe: 'bg-[color:var(--ts-pill-danger-text)]',
  },
  attention: {
    icon: TriangleAlert,
    text: 'text-[color:var(--ts-pill-warning-text)]',
    stripe: 'bg-[color:var(--ts-pill-warning-text)]',
  },
  note: { icon: Info, text: 'text-primary', stripe: 'bg-primary' },
} as const;

function FindingRow({ finding }: { finding: Finding }) {
  const { icon: Icon, text, stripe } = SEVERITY[finding.severity];
  return (
    <li className="relative flex gap-3 overflow-hidden rounded-md border border-[color:var(--ts-row-border)] py-3 pr-4 pl-5">
      <span aria-hidden="true" className={cn('absolute inset-y-0 left-0 w-1', stripe)} />
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', text)} aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-base font-medium text-foreground">
          {finding.title}
          {finding.line ? (
            <span className="ml-2 rounded bg-[color:var(--ts-muted)] px-1.5 py-0.5 font-mono text-xs font-normal text-muted-foreground">
              line {finding.line}
            </span>
          ) : null}
        </p>
        <p className="mt-0.5 text-base text-muted-foreground">{finding.detail}</p>
        {finding.fix ? <p className="mt-1 text-base text-foreground">{finding.fix}</p> : null}
      </div>
    </li>
  );
}

function NameInput({ value, label, onChange }: { value: string; label: string; onChange: (value: string) => void }) {
  return (
    <input
      aria-label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={cn(
        'h-8 w-full min-w-[9rem] rounded-md border border-[color:var(--ts-input)] bg-[color:var(--ts-surface)] px-2 text-base text-foreground',
        'hover:border-[color:var(--ts-input-hover)] focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-[color:var(--ts-focus-ring-soft)] focus-visible:outline-none',
      )}
    />
  );
}

function Footer() {
  return (
    <footer className="mt-20 border-t border-[color:var(--ts-border-soft)]">
      <div className="mx-auto flex w-full max-w-[1240px] flex-col gap-4 px-4 py-8 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="" className="h-7 w-auto opacity-80" />
          <p className="max-w-[60ch] text-sm text-muted-foreground">
            Tab Service Company files information returns with the IRS for a living. Close the tab and the file is
            gone: nothing about it is kept here.
          </p>
        </div>
        <div className="flex gap-4 text-sm">
          <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer" className="text-link hover:underline">
            Contact
          </a>
          <Link to="/sign-in" className="text-link hover:underline">
            Sign in
          </Link>
        </div>
      </div>
    </footer>
  );
}
